/**
 * Zentrale deutsche UI-Strings. Kein i18n-Framework (client.md §1) — alles hier drin.
 * Copy 1:1 aus botagent/client/mockup.html übernommen, wo vorhanden.
 */

export const STATE_LABEL: Record<string, string> = {
  ready: "Bereit",
  planned: "Plan bereit",
  running: "Läuft",
  waiting: "Wartet auf Antwort",
  budget: "Budget erschöpft",
  done: "Fertig",
  error: "Fehler",
  modified: "Geändert",
  paused: "Pausiert",
};

export const STATE_CLASS: Record<string, string> = {
  ready: "",
  planned: "st-planned",
  running: "st-running",
  waiting: "st-waiting",
  budget: "st-budget",
  done: "st-done",
  error: "st-error",
  modified: "st-done",
  paused: "st-waiting",
};

export const VIEWS = ["simple", "expert", "admin"] as const;
export type ViewMode = (typeof VIEWS)[number];
export const VIEW_LABEL: Record<ViewMode, string> = {
  simple: "Einfach",
  expert: "Experte",
  admin: "Admin",
};

export const TAB_IDS = [
  "verlauf",
  "dateien",
  "rueckfragen",
  "plan",
  "konfig",
  "logs",
  "llm",
  "kritiken",
  "suche",
  "rohstatus",
] as const;
export type TabId = (typeof TAB_IDS)[number];
export const TAB_LABEL: Record<TabId, string> = {
  verlauf: "Verlauf",
  dateien: "Dateien",
  rueckfragen: "Rückfragen",
  plan: "Plan",
  konfig: "Konfig",
  logs: "Logs",
  llm: "LLM",
  kritiken: "Kritiken",
  suche: "Suche",
  rohstatus: "Rohstatus",
};

export function tabsForView(view: ViewMode): TabId[] {
  if (view === "simple") return ["verlauf", "dateien", "rueckfragen", "suche"];
  if (view === "expert")
    return ["verlauf", "dateien", "rueckfragen", "suche", "plan", "llm", "kritiken"];
  return ["verlauf", "dateien", "rueckfragen", "suche", "plan", "llm", "kritiken", "konfig", "logs", "rohstatus"];
}

export const texte = {
  app: {
    name: "botagent",
  },
  rail: {
    home: "Startseite",
    workspacesSection: "Arbeitsbereiche",
    noWorkspaces: "Noch keine",
    newWorkspace: "＋ Neuer Arbeitsbereich",
    viewSection: "Ansicht",
    accessActive: "Zugang aktiv",
    admin: "Admin",
    noDocuments: "Noch keine Dokumente",
    downloadAll: "⤓ Alle herunterladen",
  },
  home: {
    title: "Ihre Arbeitsbereiche",
    autoRefreshHint: "Status wird automatisch alle 60 s aktualisiert",
    redeemToken: "Token einlösen…",
    newWorkspace: "＋ Neuer Arbeitsbereich",
    footerHint:
      "Jeder Arbeitsbereich hat einen eigenen Zugangslink von der Lehrkraft. Über " +
      "„Token einlösen…“ fügen Sie einen weiteren Arbeitsbereich zu dieser Liste hinzu — der Link kann auch direkt " +
      "aufgerufen und als Lesezeichen gespeichert werden.",
    lastUpdated: "zuletzt aktualisiert: gerade eben",
  },
  wizard: {
    title: "Neuer Arbeitsbereich",
    steps: ["Name & Dateien", "Aufgabe", "Einstellungen"],
    step1Title: "Name & Dateien",
    step1Sub:
      "Der Name ist cloud-weit eindeutig — z. B. Kurs oder Gruppe anhängen (z. B. pyanalysis1-kurs3).",
    workspaceNameLabel: "Name des Arbeitsbereichs",
    filesLabel: "Startdateien",
    dropzoneText: "Dateien hierher ziehen oder klicken, um Dateien auszuwählen",
    dropzoneHint:
      "xlsx → CSV · PDF → Text oder Bildseiten · Bilder → direkt · große Dateien gehen direkt in den Datei-Speicher",
    step2Title: "Aufgabe",
    step2Sub:
      "Beschreiben Sie präzise und prüfbar, wann der Arbeitsbereich FERTIG ist. Die Anleitungen " +
      "(ui_instructions.md) der Beispiele enthalten den passenden Text zum Kopieren.",
    taskLabel: "Aufgabentext",
    step3TitleSimple: "Einstellungen",
    step3SubSimple: "Die wichtigsten Optionen. Alle weiteren Werte finden Sie im Expertenmodus.",
    step3TitleExpert: "Einstellungen (Experte)",
    step3SubExpert:
      "Alle Konfigurations-Schlüssel des Cloud-Agenten. budget gilt nur bei der Erstellung.",
    budgetLabel: "Token-Budget",
    budgetHint: "Gilt nur bei der Erstellung. Später stockt eine Lehrkraft per Gutschein auf.",
    toggles: [
      {
        label: "Plan-Modus",
        hint: "Planner entwirft einen Ausführungsgraphen (Plan-Dokument, JSON), Executor-Agenten arbeiten ihn ab.",
        key: "plan",
        default: false,
      },
      {
        label: "Python-Sandbox",
        hint: "Der Agent darf Berechnungen mit run_python ausführen — präzise statt geschätzt.",
        key: "python",
        default: true,
      },
      {
        label: "Bildanalyse",
        hint: "Bilder hochladen und mit describe_image auswerten.",
        key: "image",
        default: false,
      },
      {
        label: "Interner Kritiker",
        hint: "Ein zweites Modell prüft Pläne, Ergebnisse und Fragen, bevor Sie sie sehen.",
        key: "critique",
        default: false,
      },
    ],
    cancel: "Abbrechen",
    back: "‹ Zurück",
    next: "Weiter ›",
    finishCreate: "Hochladen & Anlegen",
    finishHint: "Legt den Arbeitsbereich an, ohne ihn zu starten — „Starten“ ist danach ein eigener Schritt auf der Workspace-Seite.",
    remove: "Entfernen",
  },
  workspace: {
    createPlan: "Plan erstellen",
    start: "Start",
    resume: "Fortsetzen",
    restart: "Neustart",
    stop: "Stopp",
    maxTurns: "max. Züge",
    shareCode: "Zugangscode",
    voucher: "Gutschein",
    reload: "Status neu laden",
    budgetLabel: "Budget",
    pollingHint: "läuft — Status wird alle 5 s abgefragt",
    lastUpdated: "zuletzt aktualisiert: gerade eben",
  },
  feed: {
    taskLabel: "Aufgabe",
    questionPrefix: "Frage",
    answeredBy: "Beantwortet:",
    answerPlaceholder: "Ihre Antwort an den Agenten…",
    answerBtn: "Antworten",
    answerRequired: "Bitte zuerst eine Antwort eingeben.",
    budgetExhausted: "Budget erschöpft.",
    redeemVoucher: "Gutschein einlösen",
    askTeacher: "Bitte an die Lehrkraft wenden, damit das Budget aufgestockt wird.",
    done: "Fertig.",
    agentWorking: "Der Agent arbeitet — nächste Statusabfrage in wenigen Sekunden…",
  },
  decisions: {
    empty:
      "Noch keine Rückfragen. Der Agent stellt hier eine Frage, sobald eine Aufgabe eine " +
      "menschliche Entscheidung braucht (ask_human) — genau wie in files/decisions.md im Workspace.",
    open: "offen",
    answered: "beantwortet",
    answerLabel: "Antwort:",
    askedBy: "von",
    at: "um",
  },
  plan: {
    emptyTitle: "Noch kein Plan vorhanden.",
    emptyPlanMode:
      "Dieser Arbeitsbereich nutzt den Plan-Modus — erstellen Sie zuerst mit „Plan erstellen“ einen " +
      "Ausführungsgraphen. Der Planner stoppt danach, damit Sie den Plan lesen (und bei Bedarf ändern) " +
      "können, bevor etwas ausgeführt wird.",
    emptyClassicMode:
      "läuft im classic mode (ein Agent, eine Schleife) — es gibt keinen Ausführungsgraphen.",
    hint: "Knoten anklicken für Aufgabentext und Ergebnis. Der Plan ist ein normales Dokument (JSON) im Workspace.",
    revision: "Revision",
    produces: "erzeugt:",
    conditionResult: "condition_result:",
    branch: "Zweig:",
    fallbackSimple: "Plan nicht darstellbar.",
    statusLabel: {
      pending: "Ausstehend",
      running: "Läuft",
      done: "Fertig",
      failed: "Fehlgeschlagen",
      skipped: "Übersprungen",
      "needs-fix": "Korrektur",
    } as Record<string, string>,
  },
  config: {
    applyHint:
      "Hinweis: budget wirkt nur bei der Erstellung des Arbeitsbereichs — danach zählt allein der " +
      "Gutschein. wait_for_human ist in der Cloud fest auf exit gesetzt. expert_enabled steuert, ob " +
      "Lernende in diesem Arbeitsbereich zusätzlich die Experte-Sicht sehen (Plan, Logs, Kritiken, Suche, " +
      "Rohstatus) — die Admin-Sicht bleibt in jedem Fall der Lehrkraft vorbehalten.",
    apply: "Übernehmen",
    saved: "Konfiguration gespeichert",
    savedText: "Die Änderungen gelten ab dem nächsten Lauf.",
  },
  logs: {
    cloudLog: "Cloud-Log (S3)",
    last200: "letzte 200 Zeilen",
    last500: "letzte 500 Zeilen",
    reloadHint: "wird bei jeder Aktualisierung neu geladen",
    noLines: "— noch keine Logzeilen —",
    history: "Workspace-Historie",
    empty: "—",
  },
  llm: {
    title: "Laufende LLM-Aufrufe",
    hint: "Jeder Eintrag ist ein Aufruf, der JETZT beim Modell liegt — mit vollständigem Prompt. Er verschwindet, sobald die Antwort eintrifft.",
    emptyRunning: "— gerade liegt kein Aufruf beim Modell (zwischen zwei Aufrufen) —",
    emptyIdle: "Kein Lauf aktiv. Während eines Laufs erscheinen hier die gerade ausgeführten LLM-Aufrufe mit vollständigem Prompt-Text.",
    since: "seit",
    finishedAt: "fertig um",
    template: "Vorlage:",
    instructions: "Instructions (System-Prompt)",
    input: "Input (Prompt)",
    result: "Ergebnis",
    toolCalls: "Werkzeugaufrufe",
    emptyField: "(leer)",
    historyTitle: "Vergangene LLM-Aufrufe",
    historyHint: "Abgeschlossene Aufrufe aus diesem Lauf samt Modellantwort.",
    downloadHistory: "Verlauf herunterladen (Markdown)",
  },
  critiques: {
    emptyActive: "Noch keine Kritiken. Der interne Kritiker ist aktiv und meldet sich hier, sobald er Pläne oder Ergebnisse prüft.",
    emptyInactive: "Noch keine Kritiken. Der interne Kritiker ist in diesem Arbeitsbereich abgeschaltet (critique: false).",
    target: "Ziel:",
    action: "Aktion:",
    ok: "OK",
    rejected: "Abgelehnt",
  },
  search: {
    placeholder: "Muster, z. B. Hopper oder https://",
    button: "Suchen",
    noMatches: "Keine Treffer für",
    matchesIn: (n: number, docs: number) => `${n} Treffer in ${docs} Dokumenten`,
    openInPane: "Datei öffnen",
  },
  rawStatus: {
    hint: "Antwort von POST /api/botagent/status (vereinfacht):",
  },
  fileView: {
    back: "‹ Zur Übersicht",
    download: "Herunterladen",
    downloaded: "Heruntergeladen",
    downloadedText: "Die Datei liegt im Download-Ordner.",
    notAvailable: "— Inhalt nicht verfügbar —",
    edit: "Bearbeiten",
    save: "Speichern",
    saving: "Speichert…",
    cancel: "Abbrechen",
    saved: "Gespeichert",
    savedText: "Die Änderungen wurden in den Arbeitsbereich hochgeladen.",
  },
  files: {
    empty: "Noch keine Dokumente in diesem Arbeitsbereich.",
    dropzoneText: "Dateien hierher ziehen oder klicken, um sie hochzuladen",
    uploading: "Wird hochgeladen…",
    uploaded: "Hochgeladen",
    uploadedText: (n: number) => `${n} Datei(en) hinzugefügt.`,
    delete: "Löschen",
    deleteConfirm: (docId: string) => `„${docId}“ wirklich löschen? Das kann nicht rückgängig gemacht werden.`,
    deleted: "Gelöscht",
    deletedText: (docId: string) => `„${docId}“ wurde entfernt.`,
  },
  dialogs: {
    join: {
      title: "Token einlösen",
      label: "Link oder Zugangscode",
      hint: "Von der Lehrkraft erhalten — öffnet den zugehörigen Arbeitsbereich direkt, ohne eigenes Konto.",
      cancel: "Abbrechen",
      submit: "Einlösen",
      unknownToken: "Unbekannter Token",
      unknownTokenHint: "Bitte Link oder Code prüfen.",
    },
    share: {
      titlePrefix: "Zugangscode für",
      body: "Diesen Link an Lernende weitergeben — er öffnet genau diesen Arbeitsbereich, ohne Anmeldung:",
      hint: "Der Link kann als Lesezeichen gespeichert werden und öffnet immer denselben Arbeitsbereich.",
      copyClose: "Kopieren & schließen",
      copied: "Link kopiert",
      copiedText: "Der Zugangscode liegt in der Zwischenablage.",
    },
    voucher: {
      title: "Gutschein einlösen",
      amountLabel: "Zusätzliche Tokens",
      hint: "Gutscheine stockt üblicherweise die Lehrkraft auf. Das Budget ist bewusst knapp — es soll spürbar machen, was ein Arbeitsschritt kostet.",
      cancel: "Abbrechen",
      submit: "Einlösen",
    },
    close: "Schließen",
  },
  errors: {
    linkInvalid: "Dieser Link ist nicht (mehr) gültig — bitte einen neuen Zugangslink von der Lehrkraft anfordern.",
    accountLocked: "Konto gesperrt — bitte an die Lehrkraft wenden.",
    offline: "Keine Verbindung zum Server — wird automatisch erneut versucht.",
    budgetChecksumInvalid: "Budget-Prüfsumme ungültig — Lehrkraft informieren.",
    timeLimitReached: "Zeitlimit erreicht — mit „Fortsetzen“ arbeitet der Agent weiter.",
    runAborted: "Lauf abgebrochen",
    generic: "Ein Fehler ist aufgetreten.",
    retry: "Erneut senden",
  },
  notify: {
    agentQuestionTitle: "Der Agent hat eine Frage",
    runFinishedTitle: "Lauf abgeschlossen",
    clickToOpen: "Klicken zum Öffnen.",
  },
};
